import { For, location, onMount, reactive } from 'cdui-js';
import i18n from '../../../i18n';
import {
  fetchStockTransferInApplyPage,
  type StockTransferInApplyRecord,
  type StockTransferInWfStatus,
} from '../../../api/stockTransferIn';
import { DiFiLoading } from '../../../components/DiFi/DiFiLoading/index.tsx';
import { DiFiNoData } from '../../../components/DiFi/DiFiNoData';
import { goBackByBrowser } from '../../../lib/browser';
import { formatServerDateTime } from '../../../lib/date';
import { bootDepositApp } from '../deposit/h5-deposit/appBridge';
import { showToastError } from '../components/Toast';
import { hasNextPage } from '../utils/common';
import { StockTransferNav } from './Nav';
import {
  normalizeStockTransferApplyId,
  outBrokerNameFromApplyRecord,
  syncStockTransferAppShell,
  wfStatusLabel,
  withStockTransferQuery,
} from './shared';
import './page.css';

const PAGE_SIZE = 20;

/** 列表状态标签色：已完成绿 / 已提交蓝 / 审批中橙 / 已取消灰 */
const wfStatusTone = (status?: StockTransferInWfStatus | string) => {
  const key = String(status || '').trim().toUpperCase();
  if (key === 'FINISHED') return 'success';
  if (key === 'SUBMITTED') return 'info';
  if (key === 'CANCELLED' || key === 'STOP' || key === 'REFUSED' || key === 'BACK') return 'muted';
  return 'pending';
};

const goRecordDetail = (row: StockTransferInApplyRecord) => {
  const applyId = normalizeStockTransferApplyId(row.id);
  if (!applyId) return;
  location.routeTo(
    withStockTransferQuery('/h5/stock-transfer-in/submitted', { applyId, from: 'records' }),
    [0, 0],
  );
};

const RecordRow = (props: { row: StockTransferInApplyRecord }) => {
  const row = props.row;
  const broker = outBrokerNameFromApplyRecord(row).trim() || '—';
  const accountNo = String(row.outBrokerAccountNo || '').trim() || '—';
  const time = formatServerDateTime(row.createTime) || '—';
  const tone = wfStatusTone(row.status);

  return (
    <div
      class="sti-record-card sti-record-card-tap"
      role="button"
      tabindex="0"
      onclick={() => goRecordDetail(row)}
    >
      <div class="sti-record-head">
        <span class="sti-record-broker">{broker}</span>
        <span class={`sti-record-status sti-record-status-${tone}`}>{wfStatusLabel(row.status)}</span>
      </div>
      <div class="sti-record-meta">
        <div class="sti-record-meta-row">
          <span class="sti-record-meta-label">{i18n.StockTransferIn.LabelAccountNo}</span>
          <span class="sti-record-meta-value">{accountNo}</span>
        </div>
        <div class="sti-record-meta-row">
          <span class="sti-record-meta-label">{i18n.StockTransferIn.RecordsApplyTime}</span>
          <span class="sti-record-meta-value">{time}</span>
        </div>
      </div>
    </div>
  );
};

export const StockTransferInRecords = () => {
  const state = reactive({
    list: [] as StockTransferInApplyRecord[],
    pageNum: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    hasMore: false,
    loading: false,
    loadingMore: false,
    booted: false,
  });

  /** 与资金记录列表相同：顶栏外只留一层原生滚动，下拉不画渐变条 */
  let pullStart = 0;

  const loadPage = async (reset: boolean) => {
    if (reset) {
      if (state.loading || state.loadingMore) return;
      state.pageNum = 1;
      state.loading = state.list.length === 0;
    } else {
      if (state.loading || state.loadingMore || !state.hasMore) return;
      state.pageNum += 1;
      state.loadingMore = true;
    }
    try {
      const { list, total } = await fetchStockTransferInApplyPage({
        pageNum: state.pageNum,
        pageSize: state.pageSize,
      });
      state.total = total;
      state.list = reset ? list : [...state.list, ...list];
      state.hasMore = hasNextPage(state.total, state.pageNum, state.pageSize);
    } catch (e) {
      if (!reset) state.pageNum = Math.max(1, state.pageNum - 1);
      showToastError(e);
    } finally {
      state.loading = false;
      state.loadingMore = false;
      state.booted = true;
    }
  };

  const onScroll = (event: Event) => {
    const el = event.currentTarget as HTMLElement;
    const remain = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remain <= el.clientHeight * 0.2) void loadPage(false);
  };

  const onTouchStart = (event: TouchEvent) => {
    const el = event.currentTarget as HTMLElement;
    pullStart = el.scrollTop <= 0 ? event.touches[0]?.clientY || 0 : 0;
  };

  const onTouchEnd = (event: TouchEvent) => {
    if (!pullStart) return;
    const delta = (event.changedTouches[0]?.clientY || 0) - pullStart;
    pullStart = 0;
    if (delta > 64) void loadPage(true);
  };

  onMount(async () => {
    syncStockTransferAppShell();
    try {
      await bootDepositApp();
      await loadPage(true);
    } catch (e) {
      showToastError(e);
      state.booted = true;
    }
  });

  return (
    <div class="stock-transfer-in-h5 sti-records-page">
      <StockTransferNav title={i18n.StockTransferIn.Records} onBack={goBackByBrowser} showRecords={false} />
      <div
        class="sti-scroll sti-records-scroll"
        onscroll={onScroll}
        ontouchstart={onTouchStart}
        ontouchend={onTouchEnd}
        ontouchcancel={onTouchEnd}
      >
        {!state.booted && state.loading ? (
          <div class="sti-records-loading">
            <DiFiLoading />
          </div>
        ) : null}
        {state.booted && !state.loading && state.list.length === 0 ? (
          <div class="sti-empty">
            <DiFiNoData hasIcon text={i18n.StockTransferIn.RecordsEmpty} style={{ padding: '80px 0' }} />
          </div>
        ) : null}
        {state.list.length > 0 ? <For each={state.list}>{(row) => <RecordRow row={row} />}</For> : null}
        {state.loadingMore ? <div class="sti-records-more" /> : null}
        {state.booted && state.list.length > 0 && !state.hasMore && !state.loadingMore ? (
          <div class="sti-records-end">{i18n.FundRecords.LoadedAll}</div>
        ) : null}
      </div>
    </div>
  );
};
